import { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationHostedFormModel, EducationHostedFormSteps } from '../EducationHostedForm.types'
import { useHasCurrentPageErrors } from '../hooks/useHasCurrentPageErrors'
import { useNavigateForm } from '../hooks/useNavigateForm'
import { shouldRunEnrolmentCheck } from '../enrolmentCheck'

import { Step } from '@percent/education-hosted-form/ui/Step/Step'
import { Flex, FormField, TextInput } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { DatePicker } from '@percent/education-hosted-form/ui'

export function AgentDetails() {
  const { t } = useTranslation()
  const { progress, partnerConfig } = useEducationHostedForm()
  const { handleNextStep, handlePreviousStep } = useNavigateForm()

  // Date of birth is only asked for when the NSC lookup that consumes it will actually run.
  const enrolmentCheckEnabled = shouldRunEnrolmentCheck(partnerConfig?.configuration)
  const { errors, hasPageErrors, getStatus } = useHasCurrentPageErrors([
    {
      fieldKey: 'firstName'
    },
    {
      fieldKey: 'lastName'
    },
    {
      fieldKey: 'email'
    },
    ...(enrolmentCheckEnabled ? [{ fieldKey: 'dateOfBirth' as const }] : [])
  ])
  const { register, setValue, control } = useFormContext<EducationHostedFormModel>()

  const {
    firstName: firstNameError,
    lastName: lastNameError,
    email: emailError,
    dateOfBirth: dateOfBirthError
  } = errors

  const [dateOfBirth] = useWatch({ control, name: ['dateOfBirth'] })
  // Only surface DOB error text after the user has left the field — no noise mid-typing.
  const [dobBlurred, setDobBlurred] = useState(false)

  return (
    <Step
      step={EducationHostedFormSteps.AGENT_DETAILS}
      title={t('agentDetails.title')}
      description={t('agentDetails.description')}
      nextButtonDisabled={hasPageErrors}
      nextButtonLabel={t('button.next')}
      progress={progress}
      onNext={() => handleNextStep(EducationHostedFormSteps.OTP)}
      onBack={handlePreviousStep}
    >
      <Flex direction="column" gap={16} w="100%">
        <FormField
          label={t('agentDetails.firstName')}
          status={getStatus(firstNameError)}
          necessity="required"
          statusMessage={firstNameError}
        >
          <TextInput {...register('firstName')} placeholder={t('agentDetails.firstNamePlaceholder')} />
        </FormField>
        <FormField
          label={t('agentDetails.lastName')}
          status={getStatus(lastNameError)}
          necessity="required"
          statusMessage={lastNameError}
        >
          <TextInput {...register('lastName')} placeholder={t('agentDetails.lastNamePlaceholder')} />
        </FormField>
        <div>
          <FormField
            label={t('agentDetails.email')}
            status={getStatus(emailError)}
            necessity="required"
            statusMessage={emailError}
            description={t('agentDetails.emailDescription')}
          >
            <TextInput {...register('email')} placeholder={t('agentDetails.emailPlaceholder')} />
          </FormField>
        </div>
        {enrolmentCheckEnabled && (
          <FormField
            label={t('agentDetails.dateOfBirth')}
            status={dobBlurred ? getStatus(dateOfBirthError) : 'default'}
            necessity="required"
            statusMessage={dobBlurred ? dateOfBirthError : ''}
          >
            <DatePicker
              placeholder={t('agentDetails.dateOfBirthPlaceholder')}
              name="dateOfBirth"
              value={dateOfBirth}
              onChange={date => setValue('dateOfBirth', date ?? null, { shouldTouch: true, shouldValidate: true })}
              onBlur={() => setDobBlurred(true)}
            />
          </FormField>
        )}
      </Flex>
    </Step>
  )
}
